import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";

import Loading from "../components/common/LoaderCommon";
import NewVendorDialog from "../components/vendor-management/NewVendorDialog";
import VendorManagementList from "../components/vendor-management/VendorManagementList";
import VendorManagementPageHeader from "../components/vendor-management/VendorManagementPageHeader";
import { initialVendors } from "../components/vendor-management/vendorManagementData";
import { useCreateVendor, useGetAllVendors } from "../service/vendorService";
import { NewVendorFormValues, Vendor } from "../types/vendorManagement";
import { CreateVendorRequest, VendorResponse } from "../types/vendorTypes";

const PAGE_SIZE = 10;

const mapApiVendorToUiVendor = (vendor: VendorResponse): Vendor => {
  const fallbackVendor = initialVendors.find(
    (item) =>
      item.vendorName === vendor.vendorName ||
      item.phoneNumber === vendor.phoneNumber,
  );

  return {
    id: String(vendor.id),
    vendorName: vendor.vendorName,
    contactPerson: vendor.contactPerson,
    phoneNumber: vendor.phoneNumber,
    emailAddress: vendor.emailAddress,
    vatin: vendor.vatin,
    creditPeriod: vendor.creditPeriod,
    creditLimit: vendor.creditLimit,
    outstanding: vendor.outstanding,
    overdueAmount: vendor.overdueAmount,
    availableCredit: vendor.availableCredit,
    overdueDays: vendor.overdueDays,
    invoices: fallbackVendor?.invoices ?? [],
  };
};

function VendorManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [vendors] = useState<Vendor[]>([]);
  const [listSearchTerm, setListSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isNewVendorOpen, setIsNewVendorOpen] = useState(false);

  const {
    data: vendorsResponse,
    isLoading: isVendorsLoading,
    isError: isVendorsError,
  } = useGetAllVendors({
    search: listSearchTerm,
    status: "ACTIVE",
    pageNo: currentPage,
    pageSize: PAGE_SIZE,
  });
  const { mutate: createVendor, isPending: isCreatingVendor } =
    useCreateVendor();

  const apiVendors = useMemo(
    () => (vendorsResponse?.content ?? []).map(mapApiVendorToUiVendor),
    [vendorsResponse?.content],
  );

  const localFilteredVendors = useMemo(() => {
    const normalizedSearch = listSearchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return vendors;
    }

    return vendors.filter((vendor) =>
      [
        vendor.vendorName,
        vendor.contactPerson,
        vendor.phoneNumber,
        vendor.emailAddress,
      ].some((value) => value.toLowerCase().includes(normalizedSearch)),
    );
  }, [listSearchTerm, vendors]);

  const displayedVendors = useMemo(() => {
    if (currentPage !== 0) {
      return apiVendors;
    }

    const localIds = new Set(localFilteredVendors.map((vendor) => vendor.id));
    const mergedApiVendors = apiVendors.filter(
      (vendor) => !localIds.has(vendor.id),
    );

    return [...localFilteredVendors, ...mergedApiVendors].slice(0, PAGE_SIZE);
  }, [apiVendors, currentPage, localFilteredVendors]);

  const totalCount =
    (vendorsResponse?.totalElements ?? 0) +
    (currentPage === 0 ? localFilteredVendors.length : 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleCreateVendor = (values: NewVendorFormValues) => {
    const payload: CreateVendorRequest = {
      vendorName: values.vendorName,
      contactPerson: values.contactPerson,
      phoneNumber: values.phoneNumber,
      emailAddress: values.emailAddress,
      vatin: values.vatin,
      creditPeriod: Number(values.creditPeriod) || 0,
      creditLimit: Number(values.creditLimit) || 0,
      openingBalance: Number(values.openingBalance) || 0,
    };

    createVendor(payload, {
      onSuccess: async () => {
        setListSearchTerm("");
        setCurrentPage(0);
        setIsNewVendorOpen(false);
        await queryClient.invalidateQueries({
          queryKey: ["useGetAllVendors"],
        });
        toast.success("Vendor created successfully");
      },
      onError: (error: Error) => {
        toast.error(`Failed to create vendor: ${error.message}`);
      },
    });
  };

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top_left,rgba(26,94,249,0.12),transparent_30%),linear-gradient(180deg,#FCFDFF_0%,#F4F8FF_100%)] px-1 py-3">
      <div className="mx-auto max-w-350 space-y-6">
        <VendorManagementPageHeader
          searchTerm={listSearchTerm}
          onSearchChange={(value) => {
            setListSearchTerm(value);
            setCurrentPage(0);
          }}
          onExport={() =>
            toast.info("Export action can be wired to the API next")
          }
          onCreateVendor={() => setIsNewVendorOpen(true)}
        />
        {isVendorsLoading ? (
          <div className="rounded-3xl border border-[#E8EEF9] bg-white py-20 shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
            <Loading />
          </div>
        ) : isVendorsError ? (
          <div className="rounded-3xl border border-[#F2D9D9] bg-white px-6 py-10 text-center text-sm text-[#B15454] shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
            Unable to load vendors right now.
          </div>
        ) : (
          <VendorManagementList
            vendors={displayedVendors}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
            onSelectVendor={(vendorId) =>
              navigate(`/vendor-management/${vendorId}`)
            }
          />
        )}
      </div>

      <NewVendorDialog
        open={isNewVendorOpen}
        onOpenChange={setIsNewVendorOpen}
        onSubmit={handleCreateVendor}
        isSubmitting={isCreatingVendor}
      />
    </div>
  );
}

export default VendorManagement;
